import React, { useState, useEffect } from "react";
import {
  collectionGroup,
  onSnapshot,
  query,
  doc,
  getDoc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { auth, firestore } from "../firebase";
import {
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

const AddUser = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [username, setUsername] = useState("");
  const [userType, setUserType] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const functions = getFunctions();
  const sendUserInvite = httpsCallable(functions, "sendUserInvite");

  const userTypes = ["maintenance", "housekeeping", "manager", "frontDesk", "facilities"];

  const createUserInvite = async () => {
    if (!firstName || !lastName || !email || !userType || !selectedProperty) {
      setMessage("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      // Get selected property details
      const selectedPropertyData = properties.find(p => p.id === selectedProperty);

      const userInviteData = {
        firstName,
        lastName,
        email,
        userName: username || email.split('@')[0], // Default username from email if not provided
        userType,
        properties: [{
          displayName: selectedPropertyData?.displayName || selectedProperty,
          id: selectedProperty,
          clientId: selectedPropertyData?.clientId,
        }],
        clientId: selectedPropertyData?.clientId,
        invitedBy: auth.currentUser?.email,
        invitedAt: new Date().toISOString(),
        status: "pending"
      };

      const result = await sendUserInvite(userInviteData);

      if (result.data.success) {
        setMessage("User invitation sent successfully! They will receive an email to complete their registration.");
        // Clear form
        setFirstName("");
        setLastName("");
        setEmail("");
        setUsername("");
        setUserType("");
        setSelectedProperty("");
      } else {
        setMessage("Error sending invitation: " + (result.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error creating user invite:", error);
      setMessage("Error sending invitation: " + error.message);
    }

    setIsSubmitting(false);
  };

  useEffect(() => {
    setIsLoading(true);
    // Get list of all properties across every client via a collection-group
    // query (properties now live at Clients/{clientId}/properties/{id}).
    // The owning clientId is recovered from each doc's parent path.
    const q = query(collectionGroup(firestore, "properties"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const propertiesArray = snapshot.docs.map((doc) => ({
        id: doc.id,
        clientId: doc.ref.parent.parent?.id,
        ...doc.data(),
      }));
      //console.log(propertiesArray)
      setProperties(propertiesArray);
      setIsLoading(false);
      properties.map((property) => {
        console.log(property.id);
        console.log(property);
      });
    });

    return unsubscribe; // Unsubscribe from the listener when the component unmounts
  }, []);

  return (
    <Box p={2}>
      {isLoading ? (
        <React.Fragment>
          <Typography>Im Loading!</Typography>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <FormControl fullWidth margin="normal">
            <InputLabel id="property-select-label">Property</InputLabel>
            <Select
              labelId="property-select-label"
              value={selectedProperty}
              label="Property"
              onChange={(e) => setSelectedProperty(e.target.value)}
            >
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>
                  {property.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel id="user-type-select-label">User Type</InputLabel>
            <Select
              labelId="user-type-select-label"
              value={userType}
              label="User Type"
              onChange={(e) => setUserType(e.target.value)}
            >
              {userTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            label="Username (optional)"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            helperText="If not provided, will use the part before @ in email"
          />

          {message && (
            <Alert
              severity={message.includes("Error") ? "error" : "success"}
              sx={{ mt: 2 }}
            >
              {message}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={createUserInvite}
            disabled={isSubmitting}
            sx={{ mt: 2 }}
          >
            {isSubmitting ? "Sending Invitation..." : "Send User Invitation"}
          </Button>
        </React.Fragment>
      )}
    </Box>
  );
};

export default AddUser;
